import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatDateForInput, parseDateFromInput, formatDateForHTMLInput } from '../../../services/dateService';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { ActionButton } from '../forms';

const DateRange = ({ 
  startDate: initialStartDate, 
  endDate: initialEndDate, 
  onChange, 
  className,
  showButtons = true,
  compact = false
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  
  // Format dates for HTML date input
  const [startDate, setStartDate] = useState(formatDateForHTMLInput(initialStartDate));
  const [endDate, setEndDate] = useState(formatDateForHTMLInput(initialEndDate));

  useEffect(() => {
    setStartDate(formatDateForHTMLInput(initialStartDate));
    setEndDate(formatDateForHTMLInput(initialEndDate));
  }, [initialStartDate, initialEndDate]);

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    
    if (!showButtons) {
      onChange?.({
        start: type === 'start' ? parseDateFromInput(value) : parseDateFromInput(startDate),
        end: type === 'end' ? parseDateFromInput(value, true) : parseDateFromInput(endDate, true)
      });
    }
  };

  const handleApplyFilter = () => {
    onChange?.({
      start: startDate ? parseDateFromInput(startDate) : null,
      end: endDate ? parseDateFromInput(endDate, true) : null
    });
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    onChange?.({ start: null, end: null });
  };

  const containerClass = `flex flex-col md:flex-row items-center justify-center gap-4 ${className || ''}`;
  const groupClass = "flex items-center gap-2";
  const labelClass = `text-sm font-medium ${semantic.formLabel} whitespace-nowrap`;

  return (
    <div className={containerClass}>
      <div className={groupClass}>
        <label className={labelClass}>{replace('Start Date')}</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => handleDateChange('start', e.target.value)}
          className={`border rounded-md px-3 py-2 ${semantic.formBg} ${semantic.formText} ${semantic.formBorder} w-full md:w-auto`}
        />
      </div>
      <div className={groupClass}>
        <label className={labelClass}>{replace('End Date')}</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => handleDateChange('end', e.target.value)}
          className={`border rounded-md px-3 py-2 ${semantic.formBg} ${semantic.formText} ${semantic.formBorder} w-full md:w-auto`}
        />
      </div>
      {showButtons && (
        <div className="flex gap-2">
          <ActionButton
            action="search"
            onClick={handleApplyFilter}
            title={replace('Apply Filter')}
            showText={true}
            size="md"
            className="px-4 py-2"
          />
          <ActionButton
            action="reset"
            onClick={handleClearFilter}
            title={replace('Clear')}
            showText={true}
            size="md"
            className="px-4 py-2"
          />
        </div>
      )}
    </div>
  );
};

DateRange.propTypes = {
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  showButtons: PropTypes.bool,
  compact: PropTypes.bool
};

export default DateRange;

